import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import NowPlaying, { NowPlayingStyle } from 'components/NowPlaying';
import { Link as RouterLink } from 'react-router-dom';
import { RecoilRoot, useSetRecoilState } from 'recoil';
import songState from 'store/song';

const overlays = [
  {
    name: 'Classic',
    font: 'Zen Maru Gothic',
    description: 'A spinning Hana portrait with a warm, rounded song card.',
    path: '/overlay/1',
    style: NowPlayingStyle['087Chan'],
  },
  {
    name: 'Minimal',
    font: 'Kosugi Maru',
    description: 'A compact illustrated card with scrolling song and artist text.',
    path: '/overlay/2',
    style: NowPlayingStyle.Minimal,
  },
  {
    name: 'Minimal Mark 2',
    font: 'Kosugi Maru',
    description: 'A refined cream-and-blush card with softer edges and airy lettering.',
    path: '/overlay/5',
    style: NowPlayingStyle.MinimalMark2,
  },
  {
    name: 'Raving',
    font: 'Yomogi',
    description: 'A dark rounded card with peach accents and animated music bars.',
    path: '/overlay/3',
    style: NowPlayingStyle.Night,
  },
  {
    name: 'Sakura',
    font: 'Zen Maru Gothic',
    description: 'Soft pink gingham, cherry blossoms, and a sweet rounded song card.',
    path: '/overlay/4',
    style: NowPlayingStyle.Sakura,
  },
];

const previewSongs = [
  'Dakara Boku wa Ongaku wo Yameta (だから僕は音楽を辞めた) by Yorushika (ヨルシカ)',
  'Tada Kimi ni Hare (ただ君に晴れ) by Yorushika (ヨルシカ)',
];

const PreviewSongChange = ({ night = false }: { night?: boolean }) => {
  const setSongs = useSetRecoilState(songState);
  return (
    <Button
      size="sm"
      variant="outline"
      color={night ? '#795447' : '#a73d6b'}
      borderColor={night ? '#dcbba8' : '#efa9c7'}
      fontSize={{ base: 'xs', sm: 'sm' }}
      px={{ base: 2, sm: 3 }}
      flexShrink={0}
      position="relative"
      zIndex={1}
      onClick={() => setSongs((songs) => [{
        ...songs[0],
        name: songs[0]?.name === previewSongs[0] ? previewSongs[1] : previewSongs[0],
      }])}
    >
      {night ? '♫' : '🌸'} Preview song change
    </Button>
  );
};

const OverlayListPage = () => (
  <Box p={{ base: 4, lg: 10 }} maxW="1400px" mx="auto">
    <Text color="#9b5134" fontWeight="bold" fontSize="sm" mb={2}>
      STREAM OVERLAYS
    </Text>
    <Heading as="h1" color="#3c5556" mb={3}>
      Now-playing overlays
    </Heading>
    <Text color="#526763" maxW="640px" mb={8}>
      Choose a style to open its overlay page. Copy that page’s URL into an
      OBS browser source and use a green chroma key to remove the background.
    </Text>
    <SimpleGrid minChildWidth={{ base: '100%', xl: '380px' }} spacing={6}>
      {overlays.map((overlay) => (
        <RecoilRoot
          key={overlay.path}
          initializeState={({ set }) =>
            set(songState, [{
              name: "Boku ga Shinou to Omotta no Wa (僕が死のうと思ったのは; The Reason I Thought I'd Die) by Mika Nakashima (中島美嘉)",
              addedBy: 'Preview',
              timestamp: 1,
              vod: '',
              marker: '',
            }])
          }
        >
          <LinkBox
            bg="white"
            border="1px solid"
            borderColor="#ead8cd"
            borderRadius="xl"
            overflow="hidden"
            transition="box-shadow 0.2s, border-color 0.2s"
            _hover={{ boxShadow: 'lg', borderColor: '#c27755' }}
            _focusWithin={{ boxShadow: 'outline' }}
          >
            <Flex
              h="220px"
              align="center"
              justify="center"
              bg="#314843"
              overflow="hidden"
              pointerEvents="none"
              aria-hidden="true"
            >
              <Box flexShrink={0} transform={{ base: 'scale(0.75)', sm: 'none' }}>
                <NowPlaying style={overlay.style} draggable={false} />
              </Box>
            </Flex>
            <Box p={{ base: 4, sm: 6 }}>
              <Badge bg="#f6efe7" color="#915033" mb={3}>Now playing</Badge>
              <Heading as="h2" size="md" color="#3c5556" mb={2}>
                <LinkOverlay as={RouterLink} to={overlay.path}>
                  {overlay.name}
                </LinkOverlay>
              </Heading>
              <Text color="#526763" mb={5}>{overlay.description}</Text>
              <Text fontSize="sm" color="#526763" mb={4}>
                Font: {overlay.font}
              </Text>
              <Flex align="center" justify="space-between" gap={3} color="#9b5134">
                {(overlay.style === NowPlayingStyle.Sakura || overlay.style === NowPlayingStyle.Night) && (
                  <PreviewSongChange night={overlay.style === NowPlayingStyle.Night} />
                )}
                <Text ml="auto" fontWeight="bold" fontSize={{ base: 'xs', sm: 'sm' }} whiteSpace="nowrap">
                  Open overlay <ArrowForwardIcon ml={1} />
                </Text>
              </Flex>
            </Box>
          </LinkBox>
        </RecoilRoot>
      ))}
    </SimpleGrid>
    <Text color="#526763" fontSize="sm" mt={5}>
      Previews use a sample song. Each overlay page displays your current song.
    </Text>
  </Box>
);

export default OverlayListPage;
